import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography
} from '@mui/material';

import type { RoadmapNode } from "../../api/roadmap-service";

export type RoadmapNotesDialogProps = {
    /** The last node whose notes are shown; nothing is shown without one. */
    node?: RoadmapNode;
    onClose: () => void;
};

/**
 * The knowledge a last node requires next to the lessons its lesson groups hold, so a reader (or an author) sees
 * whether the catalogue covers it.
 */
export function RoadmapNotesDialog({ node, onClose }: RoadmapNotesDialogProps) {
    const lessons = node?.children.filter((child) => child.kind === 'LESSON') ?? [];

    return <Dialog open={ !!node } onClose={ onClose } maxWidth="md" fullWidth data-testid="roadmap-notes-dialog">
        <DialogTitle>{ node?.title }</DialogTitle>

        <DialogContent dividers>
            { !!node?.references.length && (
                <Stack direction="row" spacing={ 1 } sx={ { mb: 2, flexWrap: 'wrap' } }>
                    { node.references.map((reference) => (
                        <Chip key={ `${ reference.courseId }/${ reference.groupId ?? '' }` }
                              size="small"
                              variant="outlined"
                              label={ reference.planned ? `${ reference.title } · planned` : reference.title }
                              sx={ { borderStyle: reference.planned ? 'dashed' : 'solid' } }/>
                    )) }
                </Stack>
            ) }

            <Grid container spacing={ 3 }>
                <Grid item xs={ 12 } md={ 6 }>
                    <Typography variant="subtitle2" gutterBottom>Required knowledge</Typography>
                    <ul data-testid="roadmap-notes-list">
                        { node?.notes.map((note) => <li key={ note }><Typography variant="body2">{ note }</Typography></li>) }
                    </ul>
                </Grid>

                <Grid item xs={ 12 } md={ 6 }>
                    <Typography variant="subtitle2" gutterBottom>Lessons in the catalogue</Typography>
                    { lessons.length > 0 ? (
                        <ul>
                            { lessons.map((lesson) => (
                                <li key={ lesson.id }><Typography variant="body2">{ lesson.title }</Typography></li>
                            )) }
                        </ul>
                    ) : (
                        <Typography variant="body2" color="text.secondary">None yet: this part is planned.</Typography>
                    ) }
                </Grid>
            </Grid>
        </DialogContent>

        <DialogActions>
            <Button onClick={ onClose }>Close</Button>
        </DialogActions>
    </Dialog>;
}
